import React, { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useT } from "@/i18n/I18nContext";
import { useAuth, api } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Lightning, ArrowRight } from "@phosphor-icons/react";
import { Seo } from "@/components/Seo";

const apiErr = (err, fallback) => {
  const d = err.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

const AuthShell = ({ children, title, sub }) => (
  <div className="min-h-screen bg-zinc-950 text-zinc-100 flex" style={{ fontFamily: "Manrope, sans-serif" }}>
    <Seo
      title={`${title} — TradeMind AI`}
      description="Přihlas se do TradeMind AI — AI mentor pro tradery s analýzou grafů, obchodním deníkem a ochranou prop firm účtu. Registrace zdarma, bez karty."
    />
    <div className="hidden lg:flex flex-1 relative items-center justify-center border-r border-zinc-800 overflow-hidden">
      <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(to right, #18181b 1px, transparent 1px), linear-gradient(to bottom, #18181b 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-[120px]" />
      <div className="relative max-w-md p-12">
        <Link to="/" className="inline-flex items-center gap-2 mb-12">
          <img src="/icons/icon-192.png" alt="TradeMind AI" className="w-9 h-9 rounded-md" />
          <span className="font-semibold tracking-tight text-xl text-zinc-50">TradeMind <span className="text-blue-500">AI</span></span>
        </Link>
        <blockquote className="text-2xl font-medium tracking-tight text-zinc-200 leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>
          &ldquo;Po prvním týdnu s TradeMind AI jsem prošel prop firm challenge napoprvé. Guardian mi zachránil účet 3x.&rdquo;
        </blockquote>
        <div className="mt-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-300 font-mono font-medium">MK</div>
          <div>
            <div className="text-sm font-medium text-zinc-100">Martin K.</div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Prop Firm Funded · $100K</div>
          </div>
        </div>
      </div>
    </div>
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Link to="/" className="lg:hidden inline-flex items-center gap-2 mb-8">
          <img src="/icons/icon-192.png" alt="TradeMind AI" className="w-8 h-8 rounded-md" />
          <span className="font-semibold tracking-tight text-zinc-50">TradeMind <span className="text-blue-500">AI</span></span>
        </Link>
        <h1 className="text-3xl tracking-tight font-medium text-zinc-50" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h1>
        <p className="text-sm text-zinc-400 mt-2 mb-8">{sub}</p>
        {children}
      </div>
    </div>
  </div>
);

const Input = ({ label, ...props }) => (
  <div>
    <label className="text-sm font-medium text-zinc-400 mb-1.5 block">{label}</label>
    <input
      {...props}
      className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-4 py-2.5 text-zinc-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none placeholder-zinc-600"
    />
  </div>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a12.04 12.04 0 0 0 0 10.76l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

const GoogleButton = ({ label }) => {
  const { loginWithGoogle } = useAuth();
  return (
    <button
      type="button"
      onClick={loginWithGoogle}
      data-testid="google-signin-btn"
      className="w-full bg-zinc-100 hover:bg-white text-zinc-900 font-medium px-4 py-2.5 rounded-md transition-colors flex items-center justify-center gap-2.5"
    >
      <GoogleIcon /> {label}
    </button>
  );
};

const OrDivider = ({ label }) => (
  <div className="flex items-center gap-3 py-1">
    <div className="flex-1 h-px bg-zinc-800" />
    <span className="text-xs font-mono uppercase tracking-wider text-zinc-600">{label}</span>
    <div className="flex-1 h-px bg-zinc-800" />
  </div>
);

export const LoginPage = () => {
  const { t } = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Vítej zpět!");
      navigate(loc.state?.from || "/app");
    } catch (err) {
      toast.error(apiErr(err, "Přihlášení selhalo"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.login.title")} sub={t("auth.login.sub")}>
      <GoogleButton label={t("auth.google")} />
      <OrDivider label={t("auth.or")} />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Input data-testid="login-email" type="email" placeholder="you@trader.com" value={email} onChange={(e) => setEmail(e.target.value)} required label={t("auth.email")} />
        <Input data-testid="login-password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required label={t("auth.password")} />
        <div className="text-right -mt-1">
          <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-zinc-500 hover:text-amber-400 transition-colors">Zapomenuté heslo?</Link>
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium px-4 py-2.5 rounded-md transition-colors flex items-center justify-center gap-2">
          {loading ? "..." : t("auth.submit.login")} <ArrowRight weight="bold" size={16} />
        </button>
        <div className="text-center pt-4">
          <Link to="/register" data-testid="login-to-register" className="text-sm text-zinc-400 hover:text-blue-400 transition-colors">{t("auth.switch.toRegister")}</Link>
        </div>
      </form>
    </AuthShell>
  );
};

export const RegisterPage = () => {
  const { t } = useT();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const refCode = localStorage.getItem("tm_ref_code");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Účet vytvořen!");
      navigate("/app");
    } catch (err) {
      toast.error(apiErr(err, "Registrace selhala"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.register.title")} sub={t("auth.register.sub")}>
      {refCode && (
        <div data-testid="register-referral-banner" className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-2.5 text-sm text-amber-400">
          🎁 Registruješ se přes doporučení (kód <span className="font-mono font-semibold">{refCode}</span>)
        </div>
      )}
      <GoogleButton label={t("auth.google")} />
      <OrDivider label={t("auth.or")} />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Input data-testid="register-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required label={t("auth.name")} placeholder="Jan Novák" />
        <Input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required label={t("auth.email")} placeholder="you@trader.com" />
        <Input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required label={t("auth.password")} placeholder="min. 9 znaků" />
        <p className="text-xs text-zinc-600 -mt-2">Min. 9 znaků, 1 velké písmeno, 1 číslice, 1 speciální znak</p>
        <button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium px-4 py-2.5 rounded-md transition-colors flex items-center justify-center gap-2">
          {loading ? "..." : t("auth.submit.register")} <ArrowRight weight="bold" size={16} />
        </button>
        <div className="text-center pt-4">
          <Link to="/login" data-testid="register-to-login" className="text-sm text-zinc-400 hover:text-blue-400 transition-colors">{t("auth.switch.toLogin")}</Link>
        </div>
      </form>
    </AuthShell>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      toast.error("Něco se pokazilo, zkus to znovu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Zapomenuté heslo" sub="Zadej email a pošleme ti odkaz pro nastavení nového hesla.">
      {sent ? (
        <div data-testid="forgot-sent-message" className="bg-green-500/10 border border-green-500/30 rounded-md px-4 py-3 text-sm text-green-400">
          Pokud účet s tímto emailem existuje, poslali jsme ti odkaz pro obnovení hesla. Zkontroluj schránku (i spam). Odkaz platí 1 hodinu.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <Input data-testid="forgot-email" type="email" placeholder="you@trader.com" value={email} onChange={(e) => setEmail(e.target.value)} required label="Email" />
          <button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium px-4 py-2.5 rounded-md transition-colors flex items-center justify-center gap-2">
            {loading ? "..." : "Poslat odkaz"} <ArrowRight weight="bold" size={16} />
          </button>
        </form>
      )}
      <div className="text-center pt-6">
        <Link to="/login" data-testid="forgot-to-login" className="text-sm text-zinc-400 hover:text-blue-400 transition-colors">Zpět na přihlášení</Link>
      </div>
    </AuthShell>
  );
};

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Hesla se neshodují");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Heslo bylo změněno. Přihlas se novým heslem.");
      navigate("/login");
    } catch (err) {
      toast.error(apiErr(err, "Obnovení hesla selhalo"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Nové heslo" sub="Nastav si nové heslo ke svému účtu.">
      {!token ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-md px-4 py-3 text-sm text-red-400">
          Chybí token pro obnovení hesla. Použij odkaz z emailu.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required label="Nové heslo" placeholder="min. 9 znaků" />
          <p className="text-xs text-zinc-600 -mt-2">Min. 9 znaků, 1 velké písmeno, 1 číslice, 1 speciální znak</p>
          <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required label="Nové heslo znovu" placeholder="••••••••" />
          <button type="submit" disabled={loading} data-testid="reset-submit" className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium px-4 py-2.5 rounded-md transition-colors flex items-center justify-center gap-2">
            {loading ? "..." : "Nastavit heslo"} <ArrowRight weight="bold" size={16} />
          </button>
        </form>
      )}
      <div className="text-center pt-6">
        <Link to="/login" className="text-sm text-zinc-400 hover:text-blue-400 transition-colors">Zpět na přihlášení</Link>
      </div>
    </AuthShell>
  );
};
